import {
  FC,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AssetBody,
  AssetObject,
  SiteItem,
} from "@vviinn/user-managment-client-ts";

import { Clipboard, Plus, Trash2, RefreshCw } from "lucide-react";

import { useAppSelector } from "../../../store";
import { useNotifications } from "../../../app/useNotifications";

import { parseDateToLocalTime } from "../../../helpers";
import { Communication } from "../../details/communication";

import {
  acceptableFileFormats,
  fileTypeToAssetType,
} from "../../details/modals/helpers";
import { cdnErrors, MaxFileSize } from "./cdnErrors";
import { NotificationType } from "../../../components/Notifications";

import { Button } from "../../../ui/layout/molecules/Button";
import { DIVIDED_STYLES } from "../../../ui/layout/detailedPages/styles";
import { Badge } from "../../../ui/layout/molecules/Badge/Badge";
import { AcceptableNamedRow } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/AcceptableNamedRow";
import { addComponetsButtonStyles } from "../../../ui/layout/organisms/SlideOver/components/helpers/addComponetsButtonStyles";
import {
  DEFAULT_ICON_COLORS,
  LARGE_ICON_SIZE_STYLES,
} from "../../../ui/styles";
import { SubSectionWithStyles } from "../../../ui/layout";

export const CDNBlock: FC<{ siteDetails: SiteItem }> = ({ siteDetails }) => {
  const { notify } = useNotifications();
  const inputRef = useRef<HTMLInputElement>(null);
  const { getAssetsList, upsertAssetItem, deleteAssetItem } = Communication();

  const assets = useAppSelector((state) => state.assets.assets);
  const { companyId, id } = siteDetails;
  const [changedAsset, setChangedAsset] = useState<AssetObject | null>(null);

  useEffect(() => {
    getAssetsList(id, companyId);
  }, [id]);

  useEffect(() => {
    if (!inputRef?.current) return;
    inputRef.current.addEventListener("cancel", () => {
      setChangedAsset(null);
    });
  }, [inputRef]);

  const handleIssue = useCallback(
    (errorKind: keyof typeof cdnErrors, type: NotificationType = "Error") => {
      notify(type, cdnErrors[errorKind]);
      setChangedAsset(null);
      return;
    },
    [notify],
  );

  const removeAsset = async (assetId: AssetObject["id"]) => {
    const response = await deleteAssetItem(id, companyId, assetId);
    if (!response) return handleIssue("delete");
    getAssetsList(id, companyId);
    notify("Success", "Asset has been deleted successfully");
  };

  const copyCdnUrl = async (cdnUrl: AssetObject["cdnUrl"]) => {
    try {
      await navigator.clipboard.writeText(cdnUrl);
      notify("Success", "URL has been copied");
    } catch (error) {
      notify("Error", "Error on copying URL");
    }
  };

  const uploadFile = async (file: File) => {
    if (!file) return;
    const fileType = fileTypeToAssetType(file.type);

    if (!fileType) return handleIssue("notSupported");

    if (file.size > MaxFileSize) return handleIssue("tooBig");

    const fileName = file.name.substring(0, file.name.lastIndexOf("."));
    const isSameFileName = assets.some(({ title }) => title === fileName);
    const changedAssetSameFileType = changedAsset?.type === fileType;

    if (isSameFileName && !changedAsset)
      return handleIssue("hasSameName", "Warning");

    if (changedAsset && !changedAssetSameFileType)
      return handleIssue("hasDifferentType");

    notify("Info", "Uploading asset...");
    let assetBody: AssetBody;
    let content: string;

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = async () => {
      const parsedFile = reader.result;
      if (typeof parsedFile === "string") {
        content = parsedFile.substring(parsedFile.indexOf(","));
        if (changedAsset) {
          assetBody = {
            title: changedAsset.title,
            type: changedAsset.type,
            id: changedAsset?.id,
            content,
          };
          setChangedAsset(null);
        } else {
          assetBody = {
            title: fileName,
            type: fileType,
            content,
          };
        }

        const response = await upsertAssetItem(id, companyId, assetBody);
        if (!response) {
          notify("Error", "Error on uploading asset");
          return;
        }

        getAssetsList(id, companyId);
        notify("Success", "Asset has been uploaded successfully");
      } else return handleIssue("parseFail");
    };
    reader.onerror = () => handleIssue("parseFail");
  };

  const cdnIconsData = (
    asset: AssetObject,
  ): [string, MouseEventHandler<HTMLButtonElement>, ReactNode][] => [
    ["Copy URL", () => copyCdnUrl(asset.cdnUrl), <Clipboard />],
    [
      "Replace File",
      () => {
        setChangedAsset(asset);
        inputRef?.current?.click();
      },
      <RefreshCw />,
    ],
    ["Delete File", () => removeAsset(asset.id), <Trash2 />],
  ];

  const { buttonStyles, iconStyles, textStyles } =
    addComponetsButtonStyles(true);

  return (
    <SubSectionWithStyles title="CDN">
      <AcceptableNamedRow
        title="Files"
        message={`Supported file formats include: ${acceptableFileFormats}`}
        permissionsGroup="site:advanced_config"
        content={assets.length}
      >
        <div className="flex w-full items-center">
          <input
            type="file"
            id="file-upload"
            accept={acceptableFileFormats}
            className="hidden"
            ref={inputRef}
            onChange={({ target }) => {
              if (!target?.files) return;
              uploadFile(target.files[0]);
              target.value = "";
            }}
          />

          <label htmlFor="file-upload" className={buttonStyles}>
            <Plus className={iconStyles} />
            <span className={textStyles}>Upload New File</span>
          </label>
        </div>

        <dl className={`${DIVIDED_STYLES} empty:hidden`}>
          {assets.map((asset, key) => {
            const { title, type, lastChanged } = asset;
            return (
              <div key={key} className="flex items-center px-3 py-4">
                <Badge
                  type="neutral"
                  title={type.toUpperCase()}
                  className="mr-4 min-w-[60px] justify-center"
                />

                <dt className="text-primary truncate pr-1 text-left text-sm">
                  <p className="truncate">{title}</p>
                  {lastChanged && (
                    <p className="flex flex-wrap truncate text-xs text-stone-400">
                      <span className="mr-1">Last Modified:</span>
                      <span>{parseDateToLocalTime(lastChanged)}</span>
                    </p>
                  )}
                </dt>

                <dd className="ml-auto flex items-center gap-2">
                  {cdnIconsData(asset).map(([name, onClick, icon]) => (
                    <Button
                      key={name}
                      title={name}
                      tooltip={name}
                      onClick={onClick}
                      buttonType="iconOnly"
                      className={`${LARGE_ICON_SIZE_STYLES} ${DEFAULT_ICON_COLORS} hover:text-stone-500`}
                    >
                      {icon}
                    </Button>
                  ))}
                </dd>
              </div>
            );
          })}
        </dl>
      </AcceptableNamedRow>
    </SubSectionWithStyles>
  );
};
